import { MaintenanceWindowSelectorView } from '../view/MaintenanceWindowSelectorView';
import { templateMaintenanceWindowSelector } from '../view/template';
import { MaintenanceWindowSelectorRepository } from '../repository/MaintenanceWindowSelectorRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { MaintenanceWindowSelectorWebComponent } from '..';
import { MaintenanceWindowSelectorWebComponentViewData } from '../types';
import { queueAsyncMethodCalls } from '../../../utils/queueAsyncMethodCalls.utils';
import { DropdownEvents } from '../../../ui/gzDropdown/types';
import { GzDialogWebComponent } from '../../../ui/gzDialog';

export class MaintenanceWindowSelectorController extends BaseWebComponentController {
  public component: MaintenanceWindowSelectorWebComponent;
  public dataRepository: MaintenanceWindowSelectorRepository;
  public view: MaintenanceWindowSelectorView;
  public viewConstructor: typeof MaintenanceWindowSelectorView;
  public dataRepositoryConstructor: typeof MaintenanceWindowSelectorRepository;

  private loadingStateId: string | null = null;

  constructor(component: MaintenanceWindowSelectorWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: MaintenanceWindowSelectorView,
      viewTemplate: templateMaintenanceWindowSelector,
      dataRepositoryConstructor: MaintenanceWindowSelectorRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$selector,
        event: 'selectionChanged',
        callback: this.selectorChange.bind(this),
      },
      {
        element: this.view.dom.$selector,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.selectorOptionSelected.bind(this),
      },
      // TODO: for CERES-26375
      // {
      //   element: this.view.dom.$editButton,
      //   event: 'click',
      //   callback: this.editButtonClick.bind(this),
      // },
    ];
  }

  selectorChange(event: WebComponentEvent) {
    const selectorValue = `${event.detail.value || ''}`;

    if (this.component.getValueId() !== selectorValue) {
      // Set component value without triggering selectionChanged on the selector
      if (selectorValue !== '') {
        this.component.setRealValue({
          id: selectorValue,
          name: event.detail.title || '',
        });
      } else {
        this.component.setRealValue();
      }
    }

    if (!this.component.hidden) {
      if (selectorValue !== '' && this.view.dom.$preview) {
        this.view.dom.$previewWrapper.removeAttribute('hidden');
      } else {
        this.view.dom.$previewWrapper.setAttribute('hidden', 'hidden');
      }

      if (this.view.dom.$preview) {
        this.view.dom.$preview.maintenanceWindowId = selectorValue;
      }
    }
  }

  selectorOptionSelected(event: WebComponentEvent) {
    const { oldValue, newValue } = event.detail;

    if (oldValue.value !== '' && newValue.value === '') {
      // show dialog
      this.showConfirmationDialog(oldValue, () => {
        // restore previously selected value
        this.view.dom.$selector.setFormValue(oldValue.value);
      });
    }
  }

  showConfirmationDialog(oldValue, cancelCallback) {
    const windowBodyHtml = `<p>${this.dependencies.translateFn(
      'Removing the last maintenance window from the policy will disable the Patch Management module until you assign another maintenance window.'
    )}</p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative'>
       ${this.dependencies.translateFn('REMOVE')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('CANCEL')}
      </gz-button>`;
    const windowTitle = this.dependencies.translateFn('Remove maintenance window?');

    const confirmCallback = () => {
      // do nothing, confirm previously made change, already handled
    };

    GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      cancelCallback,
      confirmCallback
    );
  }

  // TODO: for CERES-26375
  // editButtonClick() {
  //   // TODO: remove
  //   console.log('>>> edit button click');
  //   debugger;
  // }

  async getDataForView(): Promise<MaintenanceWindowSelectorWebComponentViewData> {
    const data: MaintenanceWindowSelectorWebComponentViewData = {
      maintenanceWindows: [],
      readOnly: this.component.readOnly,
      selected: '',
    };

    const view = this.view;

    // Remove previous template states
    view.removeAllStates();

    const type = this.component.type;

    view.addPreviewComponent(type);

    // Set empty values in template
    view.clearTemplateValues();

    if (type === null) {
      return data;
    }

    if (this.component.readOnly || this.component.disabled || this.component.hidden) {
      const realValue = this.component.getRealValue();

      if (realValue) {
        data.maintenanceWindows = [
          {
            _id: realValue.id,
            name: realValue.name,
          },
        ];
        data.selected = realValue.id;
      }

      return data;
    }

    // Set loading
    view.setLoadingState();

    // Remove loading
    view.removeAllStates();

    data.maintenanceWindows = (
      await this.dataRepository.getDataForView(
        type,
        this.component.readOnly ? '' : this.component.companyId,
        this.component.getValueId()
      )
    ).maintenanceWindows;
    data.selected = this.component.getValueId();

    this.loadingStateId = null;

    return data;
  }

  async start() {
    if (this.component.type === null) {
      throw new Error('Maintenance window type not set');
    }

    super.start();
  }

  @queueAsyncMethodCalls
  async render() {
    const currentLoadingStateId = `${this.component.getValueId()}|${this.component.companyId}|${
      this.component.readOnly
    }`;

    if (this.loadingStateId === currentLoadingStateId) {
      // try to debounce requests to backend with the same parameters
      return;
    }

    this.loadingStateId = currentLoadingStateId;

    super.render();
  }
}
